import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import BrushIcon from '../../../assets/svg/brush.svg?react';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectBusinessProfileStatus } from '../../slices/business-profile/businessProfileSlice';
import ConfirmModal from '../confirm-modal/ConfirmModal';
import styles from './SupplierDetailsCard.module.scss';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useEmailField } from '../hooks/useEmailField';
import { SupplierWithId } from '../../models/supplier.model';
import RoomPreferencesOutlinedIcon from '@mui/icons-material/RoomPreferencesOutlined';
import { AccountCircleOutlined } from '@mui/icons-material';
import { updateSupplier } from '../../slices/suppliers/suppliersSlice';

interface CustomerDetailsCardProps {
  simplified?: boolean;
  preferences?: boolean;
  supplier: SupplierWithId;
}

export default function CustomerDetailsCard({
  simplified,
  preferences,
  supplier,
}: CustomerDetailsCardProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEdited, setIsEdited] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const formRef = useRef<HTMLDivElement | null>(null);
  const [popperNode] = useState<HTMLDivElement | null>(null);

  const businessProfileStatus = useAppSelector(selectBusinessProfileStatus);

  const isLoading = businessProfileStatus === 'loading';

  const toggleEditing = () => {
    setIsEdited((prev) => !prev);
  };

  const [formData, setFormData] = useState<Partial<SupplierWithId>>({
    id: supplier?.id,
    name: supplier?.name,
    registration_number: supplier?.registration_number,
    website: supplier?.website,
    physical_address: supplier?.physical_address,
    postal_address: supplier?.postal_address,
    industry: supplier?.industry,
    description: supplier?.description,
    supplier_type: supplier?.supplier_type,
    supplier_email: supplier?.supplier_email,
  });

  const cancelChanges = () => {
    setFormData({
      id: supplier?.id,
      name: supplier?.name,
      registration_number: supplier?.registration_number,
      website: supplier?.website,
      physical_address: supplier?.physical_address,
      postal_address: supplier?.postal_address,
      industry: supplier?.industry,
      description: supplier?.description,
      supplier_type: supplier?.supplier_type,
    });
    setIsEdited(false);
  };

  const saveChanges = () => {
    if (!supplier?.id) return;
    // Block the save on an invalid email address (the error is shown under the field).
    if (!emailField.validate()) return;

    const payload = {
      name: formData.name,

      registration_number: formData.registration_number,
      website: formData.website,

      physical_address: formData.physical_address,
      postal_address: formData.postal_address,
      industry: formData.industry,
      description: formData.description,
      supplier_type: formData.supplier_type,
    };

    dispatch(
      updateSupplier({
        id: supplier.id,
        company: supplier.company,
        ...payload,
      })
    );
    setIsEdited(false);
    setShowModal(false);
  };

  const confirmExit = () => {
    setIsEdited(false);
    cancelChanges();
    setShowModal(false);
  };

  const handleChange =
    <K extends keyof SupplierWithId>(field: K) =>
    (value: SupplierWithId[K]) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  // Lower cases what is typed and applies the shared basic email validation.
  const emailField = useEmailField({
    value: formData.supplier_email,
    onChange: handleChange('supplier_email'),
    disabled: !isEdited,
  });

  useEffect(() => {
    let showModalTimeout: ReturnType<typeof setTimeout> | undefined;
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isEdited &&
        formRef.current &&
        !formRef.current.contains(event.target as Node) &&
        (!popperNode || !popperNode.contains(event.target as Node))
      ) {
        const targetElement = event.target as HTMLElement;
        if (event.target instanceof HTMLElement) {
          // if (ignoredElements.includes(event.target.tagName)) return;
        }

        if (
          targetElement.closest('.MuiMenuItem-root') || // Ignore clicks on MenuItem
          targetElement.closest('.MuiSelect-root') // Ignore clicks on Select
        ) {
          return;
        }

        showModalTimeout = setTimeout(() => {
          setShowModal(true);
        }, 0);
      }
    };

    if (isEdited) document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (showModalTimeout) clearTimeout(showModalTimeout);
    };
  }, [isEdited, popperNode]);

  const supplierTypeOptions = [
    'Business Partner',
    'Client',
    'Consultant',
    'Contractor',
    'Manufacturer',
    'Supplier of goods and services',
  ];

  return (
    <Grid component='div' container className='inputs-editable'>
      <Grid component='div' size={{ xs: 12 }}>
        <Card
          className={`${styles.card} ${isEdited ? styles.card_border : ''} ${
            preferences && styles.preferences_card
          }`}
          ref={formRef}
        >
          <CardContent
            className={isLoading ? styles.card_content_min_height : ''}
            sx={{ paddingTop: 0, paddingBottom: '0 !important' }}
          >
            <Grid
              component='div'
              container
              style={{
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingTop: '16px !important',
                paddingLeft: '0 !important',
                paddingRight: '0 !important',
                paddingBottom: '16px !important',
                minHeight: '76px',
                flexWrap: 'nowrap',
                padding: '16px',
              }}
            >
              {!preferences ? (
                <Grid
                  component='div'
                  container
                  style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                >
                  <Grid
                    component='div'
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  >
                    <AccountCircleOutlined />
                  </Grid>
                  <Grid component='div'>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.customerDetails')}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Grid
                  component='div'
                  container
                  style={{ alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}
                >
                  <Grid
                    component='div'
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
                  >
                    <RoomPreferencesOutlinedIcon />
                  </Grid>
                  <Grid component='div'>
                    <Typography variant='h6' className='semibold-variant'>
                      {t('labels.supplierIndustry')}
                    </Typography>
                  </Grid>
                </Grid>
              )}
              <Grid
                component='div'
                size={{ xs: 1 }}
                container
                style={{ justifyContent: 'flex-end' }}
              >
                {!isEdited ? (
                  <Tooltip title={t('buttons.edit')} placement='top'>
                    <IconButton
                      id='supplierDetailsCardEditIconBtn'
                      onClick={toggleEditing}
                      sx={{ flexShrink: 0 }}
                    >
                      <BrushIcon id='supplierDetailsCardEditIconBtnBrushIcon' />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Button
                      id='supplierDetailsCardCancelBtn'
                      type='button'
                      className='btn-default btn-action-basic btn-cancel'
                      role='button'
                      variant='contained'
                      size='large'
                      onClick={cancelChanges}
                    >
                      {t('buttons.cancel')}
                    </Button>
                    <Button
                      id='supplierDetailsCardSaveBtn'
                      type='submit'
                      className='btn-default btn-action-basic'
                      role='button'
                      disabled={!isEdited || !formData.name}
                      variant='contained'
                      size='large'
                      onClick={saveChanges}
                    >
                      {t('buttons.save')}
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>

            {!simplified ? (
              <Grid component='div' container spacing={2}>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='supplier-name'
                    label={t('labels.supplierName')}
                    variant='outlined'
                    required={true}
                    fullWidth
                    value={formData.name ?? ''}
                    onChange={(e) => handleChange('name')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='registration-number'
                    label={t('labels.supplierRegistrationNumber')}
                    variant='outlined'
                    fullWidth
                    value={formData.registration_number ?? ''}
                    onChange={(e) => handleChange('registration_number')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='supplierEmail'
                    label={t('labels.supplierEmail')}
                    variant='outlined'
                    fullWidth
                    disabled={!isEdited}
                    {...emailField.fieldProps}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='website'
                    label={t('labels.website')}
                    variant='outlined'
                    fullWidth
                    value={formData.website ?? ''}
                    onChange={(e) => handleChange('website')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>

                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='description'
                    label={t('labels.supplierDescription')}
                    variant='outlined'
                    fullWidth
                    value={formData.description ?? ''}
                    onChange={(e) => handleChange('description')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='physical-address'
                    label={t('formInputs.physicalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.physical_address ?? ''}
                    onChange={(e) => handleChange('physical_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='postal-address'
                    label={t('formInputs.postalAddressInputLabel')}
                    variant='outlined'
                    fullWidth
                    value={formData.postal_address ?? ''}
                    onChange={(e) => handleChange('postal_address')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <FormControl fullWidth variant='outlined' disabled={!isEdited}>
                    <InputLabel htmlFor='supplierType'>{t('labels.type')}</InputLabel>
                    <Select
                      id='supplierDetailsCardChangeSupplierTypeSelect'
                      fullWidth
                      sx={{
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                      }}
                      value={formData.supplier_type}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleChange('supplier_type')(e.target.value);
                      }}
                      disabled={!isEdited}
                      displayEmpty
                      slotProps={{ input: { id: 'supplierType' } }}
                    >
                      <MenuItem
                        id='supplierDetailsCardSelectSupplierTypeMenuItem'
                        value=''
                        disabled
                      >
                        Select Supplier Type
                      </MenuItem>
                      {supplierTypeOptions.map((option) => (
                        <MenuItem
                          id={`supplierDetailsCardSupplierTypeOptionMenuItem_${option}`}
                          key={option}
                          value={option}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='industry'
                    label={t('labels.industry')}
                    variant='outlined'
                    fullWidth
                    value={formData.industry ?? ''}
                    onChange={(e) => handleChange('industry')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
              </Grid>
            ) : (
              simplified &&
              !preferences && (
                <Grid component='div' container spacing={2}>
                  <Grid component='div' size={{ xs: 12, sm: 6 }}>
                    {' '}
                    <TextField
                      id='supplier-name'
                      label={t('labels.supplierName')}
                      variant='outlined'
                      required={true}
                      fullWidth
                      value={formData.name ?? ''}
                      onChange={(e) => handleChange('name')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid component='div' size={{ xs: 12, sm: 6 }}>
                    {' '}
                    <TextField
                      id='registration-number'
                      label={t('labels.supplierRegistrationNumber')}
                      variant='outlined'
                      fullWidth
                      value={formData.registration_number ?? ''}
                      onChange={(e) => handleChange('registration_number')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid component='div' size={{ xs: 12, sm: 6 }}>
                    {' '}
                    <TextField
                      id='website'
                      label={t('labels.website')}
                      variant='outlined'
                      fullWidth
                      value={formData.website ?? ''}
                      onChange={(e) => handleChange('website')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                  <Grid component='div' size={{ xs: 12, sm: 6 }}>
                    {' '}
                    <TextField
                      id='description'
                      label={t('labels.supplierDescription')}
                      variant='outlined'
                      fullWidth
                      value={formData.description ?? ''}
                      onChange={(e) => handleChange('description')(e.target.value)}
                      disabled={!isEdited}
                    />
                  </Grid>
                </Grid>
              )
            )}
            {simplified && preferences && (
              <Grid component='div' container spacing={2}>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <FormControl fullWidth variant='outlined' disabled={!isEdited}>
                    <InputLabel id='supplier-type-label'>{t('labels.type')}</InputLabel>
                    <Select
                      id='supplierDetailsCardChangeSupplierTypeSelect2'
                      fullWidth
                      sx={{
                        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(0, 0, 0, 0.23) !important',
                        },
                      }}
                      value={formData.supplier_type}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleChange('supplier_type')(e.target.value);
                      }}
                      disabled={!isEdited}
                      displayEmpty
                      inputProps={{ 'aria-label': t('labels.type') }}
                    >
                      <MenuItem
                        id='supplierDetailsCardSelectSupplierTypeMenuItem2'
                        value=''
                        disabled
                      >
                        Select Supplier Type
                      </MenuItem>
                      {supplierTypeOptions.map((option) => (
                        <MenuItem
                          id={`supplierDetailsCardSupplierTypeOptionMenuItem2_${option}`}
                          key={option}
                          value={option}
                        >
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component='div' size={{ xs: 12, sm: 6 }}>
                  {' '}
                  <TextField
                    id='industry'
                    label={t('labels.industry')}
                    variant='outlined'
                    fullWidth
                    value={formData.industry ?? ''}
                    onChange={(e) => handleChange('industry')(e.target.value)}
                    disabled={!isEdited}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </Grid>
      <ConfirmModal open={showModal} onClose={saveChanges} onConfirm={confirmExit} />
    </Grid>
  );
}
